import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './styles.scss';
import { TextField, InputAdornment, Collapse } from '@mui/material';
import CalendarIcon from '../../../../../assets/images/CalendarIcon.svg';
import DeleteIcon from '../../../../../assets/images/delete.svg';
import ArrowIcon from '../../../../../assets/images/angle-down.svg';
import Pencil from '../../../../../assets/images/pencil.svg';

import AddIcon from '../../../../../assets/images/add_circle.svg';
import Button from '../../../../Shared/components/controls/Button';
import Loader from '../../../../IBANGeneratorComponent/Loader/index';
import config from '../../../../../Config.js';
import { withTranslation } from 'react-i18next';
import { getCSRFToken, refreshCSRFTokenIfNeeded, clearCSRFToken } from '../../../../../utils/csrf';
import { decryptData, validateEncryptionKey } from '../../../../../utils/encryptionUtils';

const TripForm = ({
  trip,
  onDelete,
  expanded,
  onToggle,
  t,
  setTripData,
  DeleteTripEndpoint,
  setLoading,
  loading,
  setIsDeleted,
  setIsFormValid,
  index,
}) => {
  // Original dates from backend (displayed in UI)
  const [originalOutboundDate] = useState(trip.outboundDate);
  const [originalInboundDate] = useState(trip.inboundDate);

  // New dates for backend submission
  const [newOutboundDate, setNewOutboundDate] = useState('');
  const [newInboundDate, setNewInboundDate] = useState('');

  const [dateError, setDateError] = useState('');
  const outboundRef = useRef(null);
  const inboundRef = useRef(null);

  const handleOutboundClick = () => {
    if (outboundRef.current) {
      outboundRef.current.showPicker();
    }
  };

  const handleInboundClick = () => {
    if (inboundRef.current) {
      inboundRef.current.showPicker();
    }
  };

  const validateDates = (outbound, inbound) => {
    if (outbound && inbound) {
      const outboundDate = new Date(outbound);
      const inboundDate = new Date(inbound);
      if (outboundDate >= inboundDate) {
        setDateError(t('validationDateErrorMsg'));
      } else {
        setDateError('');
      }
    } else {
      setDateError('');
    }
  };

  const handleOutboundChange = (e) => {
    const updatedOutboundDate = e.target.value;
    setNewOutboundDate(updatedOutboundDate);
    if (updatedOutboundDate !== originalOutboundDate) {
      setIsFormValid(true);
      setTripData(trip.id, {
        ...trip,
        outboundDate: updatedOutboundDate,
        isEdited: true,
        originalOutboundDate: originalOutboundDate, // Keep original date
      });
    }
    validateDates(updatedOutboundDate, newInboundDate);
  };

  const handleInboundChange = (e) => {
    const updatedInboundDate = e.target.value;
    setNewInboundDate(updatedInboundDate);
    if (updatedInboundDate !== originalInboundDate) {
      setIsFormValid(true);
      setTripData(trip.id, {
        ...trip,
        inboundDate: updatedInboundDate,
        isEdited: true,
        originalInboundDate: originalInboundDate,
      });
    }
    validateDates(newOutboundDate, updatedInboundDate);
  };

  const CustomCalendarIcon = () => (
    <img src={CalendarIcon} alt="Calendar" className="customCalendarIcon" />
  );

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(trip.id);
  };

  return (
    <div className={`tripContainer ${expanded ? 'expanded' : 'collapsed'}`}>
      <div className="tripHeader" onClick={() => onToggle(trip.id)}>
        <div className="TwoTitleContainer">
          <span className={`${expanded ? 'twoTitle' : 'twoTitleCollapsed'}`}>
            {t('Trip')} {index + 1} - {trip.destination}
          </span>
          {/* {expanded && (
            // <span className="twoSubTitle"> {t('twoSubTitle')}</span>
          )} */}
        </div>
        <img src={Pencil} alt="Toggle" className={`toggleArrow ${expanded ? 'expanded' : ''}`} />
      </div>

      <Collapse in={expanded}>
        <div className="tripContent">
          {/* Current Travel Details */}
          <div className="details">
            <div className="detailsTitle">
              <span>{t('detailsTitle')}</span>
            </div>
            <div className="detailsTrip">
              <div className="detailsRow">
                <div className="detailItem">
                  <span className="key">{t('OutboundDate')}:</span>
                  <span className="value">{originalOutboundDate}</span>
                </div>
                <div className="detailItem">
                  <span className="key">{t('InboundDate')}: </span>
                  <span className="value">{originalInboundDate}</span>
                </div>
              </div>
              <div className="detailsRow">
                <div className="detailItem">
                  <span className="key">{t('Destination')}:</span>
                  <span className="value">{trip.destination}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Travel Dates */}
          <div className="travelDates">
            <div className="datesHeader">
              <span>{t('TravelDates')}</span>
            </div>
            <div className="datePickersContainer">
              <div className="datePickerWrapper" onClick={handleOutboundClick}>
                <TextField
                  label={t('Outbound')}
                  type="date"
                  placeholder={t('Outbound')}
                  value={newOutboundDate}
                  onChange={handleOutboundChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    inputProps: {
                      min: new Date().toISOString().split('T')[0], // Set minimum date to today
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <CustomCalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                  className="customDatePicker"
                  inputRef={outboundRef}
                />
              </div>
              <div className="datePickerWrapper" onClick={handleInboundClick}>
                <TextField
                  label={t('Inbound')}
                  placeholder={t('Inbound')}
                  type="date"
                  value={newInboundDate}
                  onChange={handleInboundChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    inputProps: {
                      min: new Date().toISOString().split('T')[0], // Set minimum date to today
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <CustomCalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                  className="customDatePicker"
                  inputRef={inboundRef}
                />
              </div>
            </div>
            {dateError && <span className="dateError">{dateError}</span>}
            <div className="actionButtons">
              <div className="deleteTrip" onClick={handleDelete}>
                <span>{t('DeleteTrip')}</span>
                <img src={DeleteIcon} alt="delete" className="deleteIcon" />
              </div>
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

const TravelStepOne = ({
  t,
  setActiveStep,
  ExtensionLimitDetailsEndpoint,
  extensionLimitDetails,
  setExtensionLimitDetails,
  i18n,
  IsMockGenerateOTPEndpoint,
  generateOTPResponse,
  setGenerateOTPResponse,
  setExtraUserData,
  extraUserData,
  ExtraUserDetailsEndpoint,
  GenerateOTPEndpoint,
  GenerateOTPToken,
  IsMockExtraUserDetailsEndpoint,
  setTrips = { setTrips },
  trips = { trips },
  DeleteTripEndpoint = { DeleteTripEndpoint },
  isDeleted,
  setIsDeleted,
}) => {
  const [extensionLimitDetailsError, setExtensionLimitDetailsError] = useState();
  const [generateOTPResponseError, setGenerateOTPResponseError] = useState();
  const [extraUserDataError, setExtraUserDataError] = useState();
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [encryptionReady, setEncryptionReady] = useState(false);

  // Validate encryption configuration on component mount
  useEffect(() => {
    const isValid = validateEncryptionKey();
    setEncryptionReady(isValid);
    
    if (!isValid) {
     // console.error('Encryption configuration is invalid');
      setExtraUserDataError(t('EncryptionConfigError') || 'Application configuration error. Please contact support.');
    }
  }, [t]);

  useEffect(async () => {
    // Function to fetch extension limit form data
    const fetchExtensionLimitForm = async () => {
      try {
        setLoading(true);
        // Retrieve RequestId from the URL's query parameters
        const urlParams = new URLSearchParams(window.location.search);
        const requestId = urlParams.get('RequestId');

        // Ensure requestId is present
        if (!requestId) {
          window.location.href =
            '/en/personal-banking/credit-cards/Credit-Card-International-Limits';
          throw new Error('RequestId parameter is missing in the URL');
        }

        // Get the API host
        const apiHost = config.sitecoreApiHost || config.hostname || window.location.origin;
        
        // Ensure we have a valid CSRF token
        await refreshCSRFTokenIfNeeded(apiHost);
        const csrfToken = getCSRFToken();
    
        if (!csrfToken) {
          throw new Error('CSRF token not available. Please refresh the page and try again.');
        }

        const endpointUrl = `${config.hostname}${ExtensionLimitDetailsEndpoint}`;

        // Fetch data with POST method and CSRF token
        const response = await fetch(endpointUrl, {
          method: 'POST',
          credentials: 'include', // Important for cookies
          headers: {
            'Content-Type': 'application/json',
            '__RequestVerificationToken': csrfToken,
          },
          body: JSON.stringify({
            RequestId: requestId,
          })
        });

        // Handle CSRF token validation failure
        if (response.status === 403) {
        //  console.warn('CSRF validation failed. Clearing token...');
          clearCSRFToken();
          throw new Error('Security validation failed. Please refresh the page and try again.');
        }

        if (response.status === 401) {
          throw new Error('Authentication required. Please refresh the page and try again.');
        }

        // Check if the response is ok (status in the range 200-299)
       // console.log(response.body);
        if (!response.ok) {
        //  console.log(response.body);
          throw new Error('Network response was not ok');
        }

           const encryptedResponse = await response.json();
  // Check if response contains encrypted data
            if (encryptedResponse.Data && encryptedResponse.Data.CipherText && encryptedResponse.Data.IV) {
            //  console.log('Encrypted response received, decrypting...');
              
              try {
                // Decrypt the data using key from config
                const decryptedData = await decryptData(encryptedResponse.Data);
                setExtensionLimitDetails(decryptedData.Item2); // Set the response data to state
          const mappedTrips = decryptedData?.Item2?.Trips?.map((trip, index) => ({
            id: trip.TripID,
            expanded: index === 0, // Default value for `expanded`
            destination: trip.Country,
            outboundDate: new Date(trip.Outbound).toLocaleDateString(),
            inboundDate: new Date(trip.Inbound).toLocaleDateString(),
          }));
 
          setTrips(mappedTrips); 

              } catch (decryptError) {
              //  console.error('Decryption error:', decryptError);
                throw new Error('Failed to decrypt response data. Please try again or contact support.');
              }
            } else { 
            
           window.location.href =
            '/en/personal-banking/credit-cards/Credit-Card-International-Limits';

            }


        
      } catch (error) {
        setExtensionLimitDetailsError(error.message); // Handle error
       // console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    await fetchExtensionLimitForm();
  }, [0]); // Empty dependency array to run only once on component mount

  const handleGenerateOTP = async () => {
    // Check if encryption is ready before proceeding
    if (!encryptionReady) {
      setExtraUserDataError(t('EncryptionConfigError') || 'Encryption is not properly configured');
      return;
    }

    const isPassportNumber = !!extensionLimitDetails.PassportNumber;
    const referenceNumber = isPassportNumber
      ? extensionLimitDetails.PassportNumber
      : extensionLimitDetails.NationalId;
    setLoading(true);
    try {
      const userDetails = await GetExtraUserDetails(referenceNumber, isPassportNumber);
    //  console.log(`Extra User Details response: ${userDetails}`);
      if (userDetails) {
        const otpResponse = await GenerateOTP(userDetails.CustomerNo);
       // console.log(`Generate OTP Response: ${otpResponse}`);

        if (otpResponse && otpResponse.output.success === '1') {
          setGenerateOTPResponse(otpResponse);
          setActiveStep(2);
          setLoading(false);
        } else {
          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);
      console.error('Error generating OTP:', error);
      // Optionally set an error state or show a notification to the user
    } finally {
      setLoading(false);
    }
  };

  const setTripData = (tripNumber, tripData) => {
    setTrips((prevTrips) =>
      prevTrips.map((trip) =>
        trip.id === tripNumber ? { ...trip, ...tripData, isEdited: true } : trip
      )
    );
  };

  const GetExtraUserDetails = async (referenceNumber, isPassportNumber) => {
    try {
      // Get the API host
      const apiHost = config.sitecoreApiHost || config.hostname || window.location.origin;
      
      // Ensure we have a valid CSRF token
      await refreshCSRFTokenIfNeeded(apiHost);
      const csrfToken = getCSRFToken();
  
      if (!csrfToken) {
        throw new Error('CSRF token not available. Please refresh the page and try again.');
      }
  
      const url = `${config.hostname}${ExtraUserDetailsEndpoint}`;
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Important for cookies
        headers: {
          'Content-Type': 'application/json',
          '__RequestVerificationToken': csrfToken,
        },
        body: JSON.stringify({
          ReferenceNumber: referenceNumber,
          IsPassportNumber: isPassportNumber,
          Lang: i18n.language,
          IsMock: IsMockExtraUserDetailsEndpoint || false
        })
      });
  
      // Handle CSRF token validation failure
      if (response.status === 403) {
        //console.warn('CSRF validation failed. Clearing token...');
        clearCSRFToken();
        throw new Error('Security validation failed. Please refresh the page and try again.');
      }
  
      if (response.status === 401) {
        throw new Error('Authentication required. Please refresh the page and try again.');
      }
  
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
      }
  
      const encryptedResponse = await response.json();
      
      // Check if response contains encrypted data
      if (encryptedResponse.Data && encryptedResponse.Data.CipherText && encryptedResponse.Data.IV) {
       // console.log('Encrypted response received, decrypting...');
        
        try {
          // Decrypt the data using key from config
          const decryptedData = await decryptData(encryptedResponse.Data);
        // console.log('Data decrypted successfully');
          
          setExtraUserData(decryptedData); // Set the user data to state
          return decryptedData; // Return data for further processing
        } catch (decryptError) {
        //  console.error('Decryption error:', decryptError);
          throw new Error('Failed to decrypt response data. Please try again or contact support.');
        }
      } else {
        // Handle non-encrypted error responses
      //  console.log('Non-encrypted response:', encryptedResponse);
        
        if (encryptedResponse.Message) {
          throw new Error(encryptedResponse.Message);
        }
        
        // Fallback: if response has Data but no encryption, use it directly (backward compatibility)
        if (encryptedResponse) {
          setExtraUserData(encryptedResponse); // Set the user data to state
          return encryptedResponse; // Return data for further processing
        }
        
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
     // console.error('Error in GetExtraUserDetails:', error);
      
      // Set user-friendly error messages
      if (error.message.includes('CSRF') || error.message.includes('Security')) {
        setExtraUserDataError(t('SecurityErrorMsg') || 'Security validation failed. Please refresh the page.');
      } else if (error.message.includes('Authentication')) {
        setExtraUserDataError(t('AuthenticationErrorMsg') || 'Authentication required. Please refresh the page.');
      } else if (error.message.includes('decrypt') || error.message.includes('Encryption')) {
        setExtraUserDataError(t('DecryptionErrorMsg') || 'Failed to process secure data. Please try again.');
      } else if (error.message.includes('configured')) {
        setExtraUserDataError(t('ConfigErrorMsg') || 'Application is not properly configured. Please contact support.');
      } else {
        setExtraUserDataError(t('GenericErrorMsg') || error.message);
      }
      
      throw error; // Rethrow to handle in the calling function
    }
  };
  

  // const GetExtraUserDetails = async (referenceNumber, isPassportNumber) => {
  //   try {
  //     // const url = `https://fabheadlessjsssc.dev.local/api/extrauserdata/GetExtraUserDetails?ReferenceNumber=${referenceNumber}&IsPassportNumber=${isPassportNumber}&Lang=${i18n.language}&IsMock=true`;
  //     const url = `${config.hostname}${ExtraUserDetailsEndpoint}?ReferenceNumber=${referenceNumber}&IsPassportNumber=${isPassportNumber}&Lang=${i18n.language}&IsMock=${IsMockExtraUserDetailsEndpoint}`;
  //     const response = await fetch(url);

  //     if (!response.ok) {
  //       throw new Error('Failed to fetch extra user details');
  //     }

  //     const data = await response.json();
  //     setExtraUserData(data); // Set the user data to state
  //     return data; // Return data for further processing
  //   } catch (error) {
  //     setExtraUserDataError(error.message);
  //     throw error; // Rethrow to handle in the calling function
  //   }
  // };

  const GenerateOTP = async (customerNo) => {
    try {
      //const url = `https://fabheadlessjsssc.dev.local/api/ibanotp/generate?mock=1&reference=${customerNo}`;
      const url = `${config.hostname}${GenerateOTPEndpoint}?mock=${IsMockGenerateOTPEndpoint}&reference=${customerNo}&token=${GenerateOTPToken}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to generate OTP');
      }

      const data = await response.json();
      if (data?.output?.success === '1') {
        setGenerateOTPResponse(data); // Set the OTP response to state
        return data; // Return data for further processing
      } else {
        setGenerateOTPResponseError(t('otpGenerationFailed'));
        return null;
      }
    } catch (error) {
      setGenerateOTPResponseError(error.message);
      throw error; // Rethrow to handle in the calling function
    }
  };

  const deleteTrip = (id) => {
    const visibleTrips = trips.filter((trip) => !trip.isDeleted);

    // Mark the trip as deleted in state
    setTrips((prevTrips) =>
      prevTrips.map((trip) =>
        trip.id === id ? { ...trip, isDeleted: true, expanded: false } : trip
      )
    );

    // If this was the last visible trip, show warning
    if (visibleTrips.length === 1) {
      setWarningMessage(t('DeleteLastTripWarning'));
    } else {
      setWarningMessage('');
    }
    // Update the isDeleted state based on the id of the trip
    setIsDeleted((prevIsDeleted) => ({
      ...prevIsDeleted,
      [id]: true,
    }));
  };

  const toggleTrip = (id) => {
    setTrips((prevTrips) =>
      prevTrips.map((trip) => ({
        ...trip,
        expanded: trip.id === id ? !trip.expanded : false,
      }))
    );
  };

  if (loading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="stepOneContainer">
        
      {/* Personal Details Card */}
      <div className="personCard">
        <div className="header">
          <span>{t('PersonalDetailsTripDate')}</span>
        </div>
        <div className="firstRow">
          <div className="info">
            <span className="key">{t('Name')}:</span>
            <span className="value">{extensionLimitDetails?.ClientName}</span>
          </div>
          <div className="info">
            <span className="value">{t('PhoneNumber')}: </span>
            <span className="key">{extensionLimitDetails?.PhoneNumber}</span>
          </div>
        </div>
        <div className="firstRow">
          <div className="info">
            <span className="key">
              {extensionLimitDetails?.NationalId
                ? t('NationalIDNumber') + ':'
                : t('PassportNumber') + ':'}
            </span>
            <span className="value">
              {extensionLimitDetails?.NationalId ||
                extensionLimitDetails?.PassportNumber}
            </span>
          </div>
          <div className="info">
            <span className="value">{t('CardNumber')}: </span>
            <span className="key">
              **** **** **** {extensionLimitDetails?.CreditCardNumber}
            </span>
          </div>
        </div>
      </div>

      {/* Travel Details Container */}
      <div className="travelDetails">
        <div className="headerContainer">
          <span className="detailsTitle">{t('TravelDetails')}</span>
          <span className="detailSubTitle">{t('TravelDetailsSubtitle')}</span>
        </div>
        {warningMessage && (
          <div className="warningMessage">
            <span>{warningMessage}</span>
          </div>
        )}
        {/* Trip Forms */}
        {trips
          .filter((trip) => !trip.isDeleted)
          .map((trip, index) => (
            <TripForm
              key={trip.id}
              trip={trip}
              index={index}
              onDelete={() => deleteTrip(trip.id)}
              expanded={trip.expanded}
              onToggle={() => toggleTrip(trip.id)}
              t={t}
              setTripData={setTripData}
              DeleteTripEndpoint={DeleteTripEndpoint}
              setLoading={setLoading}
              loading={loading}
              setIsFormValid={setIsFormValid}
              setIsDeleted={setIsDeleted}
            />
          ))}
        <div className="error-message">{generateOTPResponseError}</div>
        {extraUserDataError && (
          <div className="error-message">{extraUserDataError}</div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="btnContainer">
        <Button 
          onClick={() => handleGenerateOTP()}
          disabled={!encryptionReady}
        >
          {t('VERIFYChanges')}
        </Button>
      </div>
    </div>
  );
};

TripForm.propTypes = {
  trip: PropTypes.shape({
    id: PropTypes.number.isRequired,
    expanded: PropTypes.bool.isRequired,
    destination: PropTypes.string.isRequired,
    outboundDate: PropTypes.string,
    inboundDate: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(TravelStepOne);
